import { 
  useGetMetaTemplatesQuery,
  useCreateMetaAdsTemplateMutation,
  useUpdateMetaAdsTemplateMutation,
  useRemoveMetaAdsTemplateMutation,
  useCreateMetaAdsRecordMutation,
  useUpdateMetaAdsTemplateAssigneeMutation
} from "../../../../generated/graphql";
import CreateMarkupButton from "../../markupSetting/CreateMarkupButton";
import MetaAccountSelect from "../meta/MetaAccountSelect";
import MetaCampaignSelect from "../meta/MetaCampaignSelect";
import { AdsPlatformAdapter, ReportListItem, MetricsGroup } from "./types";
import { ReportList as MetaReportList } from "../meta/MetaReportList";

const reportList: ReportListItem[] = MetaReportList.map(r => ({
  name: r.name,
  title: r.title,
  fields: r.fields
}));

const FieldMap: Record<string, string> = {
  customer: "帳戶",
  campaign: "行銷活動",
  adset: "廣告組合",
  ad: "廣告",
  age: "年齡",
  gender: "性別",
  impression_device: "裝置",
  publisher_platform: "平台",
  region: "區域",
  country: "國家/地區",
  hourly_stats_aggregated_by_audience_time_zone: "時段",
  total: "總覽",
  daily: "每日",
};

// 從 CreateMetaTemplateView 提取完整的 metricsFieldsMap 並轉換為 metricsGroups
const metricsFieldsMap = {
  基本: [
    {field: "reach", label: "觸及人數"},
    {field: "impressions", label: "曝光次數"},
    {field: "spend", label: "花費金額"},
  ],
  觸及: [
    {field: "spend/reach", label: "千次觸及成本"},
    {field: "reach/impressions", label: "觸及率"},
  ],
  曝光: [
    {field: "spend/impressions", label: "CPM"},
    {field: "frequency", label: "頻率"},
  ],
  互動: [
    {field: "actions.post_engagement", label: "互動次數"},
    {field: "actions.post_engagement/impressions", label: "互動率"},
    {field: "spend/actions.post_engagement", label: "每次貼文互動成本"},
    {field: "actions.post_reaction", label: "心情數"},
    {field: "actions.comment", label: "留言數"},
    {field: "actions.post", label: "分享次數"},
    {field: "actions.post_engagement/clicks", label: "貼文互動次數/全部點擊"},
    {field: "actions.post_engagement/actions.link_click", label: "貼文互動次數/連結點擊"},
    {field: "actions.page_engagement", label: "粉絲專頁互動"},
    {field: "spend/actions.page_engagement", label: "每次粉絲專頁互動成本"},
    {field: "actions.like", label: "追蹤或按讚"},
    {field: "spend/actions.like", label: "每次追蹤或按讚成本"},
  ],
  訊息: [
    {field: "actions.onsite_conversion.messaging_conversation_started_7d", label: "訊息對話開始次數"},
    {field: "spend/actions.onsite_conversion.messaging_conversation_started_7d", label: "每次展開訊息對話成本"},
    {field: "actions.onsite_conversion.messaging_conversation_started_7d/impressions", label: "訊息對話開始次數/曝光次數"},
    {field: "actions.onsite_conversion.messaging_conversation_started_7d/clicks", label: "訊息對話開始次數/全部點擊"},
    {field: "actions.onsite_conversion.messaging_conversation_started_7d/actions.link_click", label: "訊息對話開始次數/連結點擊"},
  ],
  影音內容: [
    {field: "video_thruplay_watched_actions.video_view", label: "ThruPlay 次數"},
    {field: "video_thruplay_watched_actions.video_view/impressions", label: "收視率"},
    {field: "spend/video_thruplay_watched_actions.video_view", label: "每次 ThruPlay 成本"},
    {field: "video_continuous_2_sec_watched_actions.video_view", label: "影片連續播放 2 秒以上的次數"},
    {field: "actions.video_view", label: "影片播放 3 秒以上的次數"},
    {field: "video_p25_watched_actions.video_view", label: "影片播放到 25% 的次數"},
    {field: "video_p50_watched_actions.video_view", label: "影片播放到 50% 的次數"},
    {field: "video_p75_watched_actions.video_view", label: "影片播放到 75% 的次數"},
    {field: "video_p95_watched_actions.video_view", label: "影片播放到 95% 的次數"},
    {field: "video_p100_watched_actions.video_view", label: "影片播放到 100% 的次數"},
    {field: "spend/video_continuous_2_sec_watched_actions.video_view", label: "每次影片連續播放 2 秒以上的成本"},
    {field: "spend/actions.video_view", label: "每次影片播放 3 秒以上的成本"},
    {field: "video_avg_time_watched_actions", label: "影片平均播放時間"},
  ],
  點擊次數: [
    {field: "clicks", label: "點擊次數（全部）"},
    {field: "clicks/impressions", label: "CTR（全部）"},
    {field: "spend/clicks", label: "CPC（全部）"},
    {field: "actions.link_click", label: "連結點擊次數"},
    {field: "actions.link_click/impressions", label: "CTR（連結點閱率）"},
    {field: "spend/actions.link_click", label: "CPC（單次連結點擊成本）"},
    {field: "actions.landing_page_view", label: "連結頁面瀏覽次數"},
    {field: "spend/actions.landing_page_view", label: "每次連結頁面瀏覽成本"},
    {field: "actions.profile_view", label: "Instagram 商業檔案瀏覽"},
  ],
  知名度: [
    {field: "estimated_ad_recallers", label: "廣告回想提升估計值"},
    {field: "estimated_ad_recall_rate", label: "廣告回想提升率估計值"},
    {field: "spend/estimated_ad_recallers", label: "每次廣告回想提升估計值成本"},
  ],
  內容瀏覽: [
    {field: "actions.view_content", label: "內容瀏覽次數"},
    {field: "spend/actions.view_content", label: "每次內容瀏覽的成本"},
  ],
  註冊: [
    {field: "actions.omni_complete_registration", label: "完成註冊次數"},
    {field: "spend/actions.omni_complete_registration", label: "每次完成註冊的成本"},
    {field: "actions.omni_complete_registration/clicks", label: "註冊/全部點擊"},
    {field: "actions.omni_complete_registration/actions.link_click", label: "註冊/連結點擊"},
  ],
  潛在顧客: [
    {field: "actions.lead", label: "潛在顧客"},
    {field: "spend/actions.lead", label: "每位潛在顧客取得成本"},
    {field: "actions.lead/clicks", label: "潛在顧客/全部點擊"},
    {field: "actions.lead/actions.link_click", label: "潛在顧客/連結點擊"},
    {field: "actions.website_lead", label: "網站潛在顧客"},
    {field: "actions.offline_conversion", label: "離線潛在顧客"},
    {field: "actions.leadgen", label: "Meta 潛在顧客"},
  ],
  提交申請: [
    {field: "actions.submit_application_total", label: "申請表提交次數"},
    {field: "spend/actions.submit_application_total", label: "每次提交申請的成本"},
    {field: "actions.submit_application_total/clicks", label: "提交申請/全部點擊"},
    {field: "actions.submit_application_total/actions.link_click", label: "提交申請/連結點擊"},
  ],
  訂閱: [
    {field: "actions.subscribe_total", label: "訂閱"},
    {field: "spend/actions.subscribe_total", label: "每次訂閱成本"},
    {field: "actions.subscribe_total/clicks", label: "訂閱/全部點擊"},
    {field: "actions.subscribe_total/actions.link_click", label: "訂閱/連結點擊"},
  ],
  購買: [
    {field: "actions.omni_purchase", label: "購買"},
    {field: "spend/actions.omni_purchase", label: "每次購買的成本"},
    {field: "actions.omni_purchase/clicks", label: "購買/全部點擊"},
    {field: "actions.omni_purchase/actions.link_click", label: "購買/連結點擊"},
    {field: "action_values.omni_purchase", label: "購買轉換值"},
    {field: "action_values.omni_purchase/spend", label: "購買 ROAS"},
    {field: "actions.omni_add_to_cart", label: "加到購物車次數"},
    {field: "spend/actions.omni_add_to_cart", label: "每次加到購物車的成本"},
  ],
  CPAS: [
    {field: "catalog_segment_actions.omni_purchase", label: "CPAS 購買次數"},
    {field: "spend/catalog_segment_actions.omni_purchase", label: "CPAS 購買成本"},
    {field: "catalog_segment_actions.omni_purchase/clicks", label: "CPAS 購買次數/全部點擊"},
    {field: "catalog_segment_actions.omni_purchase/actions.link_click", label: "CPAS 購買次數/連結點擊"},
    {field: "catalog_segment_value.omni_purchase", label: "CPAS 購買轉換值"},
    {field: "catalog_segment_value.omni_purchase/spend", label: "CPAS ROAS"},
    {field: "catalog_segment_actions.omni_add_to_cart", label: "CPAS 加到購物車次數"},
    {field: "spend/catalog_segment_actions.omni_add_to_cart", label: "CPAS 每次加到購物車的成本"},
    {field: "catalog_segment_actions.view_content", label: "CPAS 內容瀏覽次數"},
  ],
  應用程式: [
    {field: "actions.omni_activate_app", label: "啟用應用程式"},
    {field: "spend/actions.omni_activate_app", label: "每次應用程式啟用成本"},
    {field: "actions.app_install", label: "應用程式安裝"},
    {field: "spend/actions.app_install", label: "每次應用程式安裝成本"},
  ]
};

const metricsGroups: MetricsGroup[] = Object.keys(metricsFieldsMap).map(title => ({
  title,
  fields: metricsFieldsMap[title]
}));

// formatField 函數：先檢查 FieldMap，再檢查 metricsFieldsMap
const formatField = (field: string): string => {
  // 先檢查 FieldMap（包括組合字段）
  if (FieldMap[field]) {
    return FieldMap[field];
  }
  // 再檢查 metricsFieldsMap
  for (const category in metricsFieldsMap) {
    const found = metricsFieldsMap[category].find(item => item.field === field);
    if (found) {
      return found.label;
    }
  }
  // 如果都沒找到，返回原字段
  return field;
};

export const metaAdapter: AdsPlatformAdapter = {
  key: 'meta',
  displayName: 'Meta Ads',
  nameLabel: '報表名稱',
  tableNameHeader: 'Meta 報表名稱',
  createButtonText: '新增Meta報表設定',
  generateQueryEntity: 'metaAds',
  removeUnexposedLabel: '移除所有未曝光資料',

  formatField,
  reportList,
  metricsGroups,

  AccountSelect: MetaAccountSelect,
  CampaignSelect: MetaCampaignSelect,
  MarkupButton: () => <CreateMarkupButton platform="meta" />,

  useGetTemplatesQuery: () => {
    const { data, loading, error } = useGetMetaTemplatesQuery();
    return { data: data ? { templates: data?.metaAdsTemplates! } : undefined, loading, error };
  },
  useCreateTemplateMutation: () => {
    const [mutate] = useCreateMetaAdsTemplateMutation();
    return [mutate];
  },
  useUpdateTemplateMutation: () => {
    const [mutate] = useUpdateMetaAdsTemplateMutation();
    return [mutate];
  },
  useRemoveTemplateMutation: () => {
    const [mutate] = useRemoveMetaAdsTemplateMutation();
    return [mutate];
  },
  useCreateRecordMutation: () => {
    const [mutate] = useCreateMetaAdsRecordMutation();
    return [mutate];
  },
  useUpdateTemplateAssigneeMutation: () => {
    const [mutate] = useUpdateMetaAdsTemplateAssigneeMutation();
    return [mutate];
  },

  refetchTemplatesQueryName: "getMetaTemplates",
  refetchRecordListQueryName: "getMetaAdsRecordList",
  recordInputKey: "metaAdsRecordInput",
  templateInputKey: "metaAdsTemplateInput",

  buildTemplateInput: ({ name, reportNames, metricsFields, accountCampaigns, assigneeId, removeUnexposed }) => ({
    name, 
    reportNames, 
    metricsFields, 
    accountCampaigns, 
    assigneeId, 
    removeUnexposed
  }),
};
